import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable, type DataTableSummary } from '@aeg-presents/shared-components/DataTable';
import { TicketAllocation, TicketShow } from '@app-types/ticketSales';
import { formatAbsoluteNumber, formatFeedSales } from '@utils/numberFormatters';
import { allocationTableColumns } from './AllocationTable.columns';
import {
  isManualSalesEditable,
  sortAllocations,
  sumAllocationTotals,
  withRecalculatedSales,
} from './AllocationTable.utils';

function formatSummaryValue(value: number | null): string {
  return formatAbsoluteNumber(value, '-');
}

export type AllocationTableProps = {
  show: TicketShow;
  visible?: boolean;
};

function toRows(allocations: TicketShow['allocations']): TicketAllocation[] {
  return sortAllocations(allocations).map((allocation) => ({ ...allocation }));
}

export function AllocationTable({ show, visible = true }: AllocationTableProps) {
  const [rows, setRows] = useState<TicketAllocation[]>(() => toRows(show.allocations));

  useEffect(() => {
    setRows(toRows(show.allocations));
  }, [show.allocations]);

  const totals = useMemo(() => sumAllocationTotals(rows), [rows]);

  const summary = useMemo<DataTableSummary>(
    () => ({
      visible: true,
      id: `${show.id}-show-total`,
      label: 'Show Total',
      labelField: 'ticketCompany',
      cells: [
        { field: 'ticketType', visible: false },
        { field: 'ticketName', visible: false },
        { field: 'allocation', value: formatSummaryValue(totals.allocation) },
        { field: 'price', visible: false },
        { field: 'lastUpdated', visible: false },
        { field: 'feedSales', value: formatFeedSales(totals.feedSales) },
        { field: 'manualSales', value: formatSummaryValue(totals.manualSales) },
        { field: 'totalSales', value: formatSummaryValue(totals.totalSales) },
        { field: 'percentSold', value: totals.percentSold },
        { field: 'unsold', value: formatSummaryValue(totals.unsold) },
      ],
    }),
    [show.id, totals],
  );

  const processRowUpdate = useCallback((newRow: TicketAllocation) => {
    const parsedManualSales = Number(newRow.manualSales);
    const manualSales = Number.isFinite(parsedManualSales) ? parsedManualSales : 0;
    const updatedRow = withRecalculatedSales(newRow, manualSales);

    setRows((current) => current.map((row) => (row.id === updatedRow.id ? updatedRow : row)));

    return updatedRow;
  }, []);

  return (
    <DataTable
      visible={visible}
      rows={rows}
      columns={allocationTableColumns}
      summary={summary}
      isCellEditable={(row, field) => field === 'manualSales' && isManualSalesEditable(row)}
      processRowUpdate={processRowUpdate}
      dataTestID={`allocation-table-${show.id}`}
    />
  );
}
